import { useRef, useState, useEffect, useMemo } from "react";
import { FaUser, FaCog, FaShieldAlt, FaCreditCard, FaTimes, FaPen } from "react-icons/fa";
import "./Profile.css";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, storage, auth } from "../../firebase"; // adjust path if needed
import { sendPasswordResetEmail, updatePassword, deleteUser, reauthenticateWithCredential, EmailAuthProvider, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Subscription from "./Profile Components/Subscription";

const Profile = ({ userId, userData, websites, onClose }) => {
  const [activeView, setActiveView] = useState("profile");
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [avatarFile, setAvatarFile] = useState(null); // holds selected file
  const [avatarPreview, setAvatarPreview] = useState(""); // preview URL
  const [avatarUploading, setAvatarUploading] = useState(false);

  // ✅ Editable profile fields in state (so we can detect changes + save)
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");

  // ✅ Keep an "original snapshot" so we can check if anything changed
  const [original, setOriginal] = useState(null);

  // ✅ Populate state when userData loads/changes
  useEffect(() => {
    if (!userData) return;

    setName(userData?.name || "");
    setSurname(userData?.surname || "");
    setPhone(userData?.phone || "");
    setUsername(userData?.username || "");
    setBio(userData?.bio || "");

    setOriginal({
      name: userData?.name || "",
      surname: userData?.surname || "",
      phone: userData?.phone || "",
      username: userData?.username || "",
      bio: userData?.bio || "",
      picture: userData?.picture || "",
    });

    // reset avatar selection when switching users / reopening profile
    setAvatarFile(null);
    setAvatarPreview("");
  }, [userData]);

  // ✅ When user picks an image
  const handlePickAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Optional: basic validation
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image is too large. Please use an image under 5MB.");
      return;
    }

    setAvatarFile(file);

    // Preview in UI
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
  };

  // ✅ Utility
  const normalize = (v) => (v ?? "").toString().trim();

  // ✅ Check if ANY profile field changed (or avatar changed)
  const hasChanges = () => {
    if (!original) return false;

    const fieldsChanged =
      normalize(name) !== normalize(original.name) ||
      normalize(surname) !== normalize(original.surname) ||
      normalize(phone) !== normalize(original.phone) ||
      normalize(username) !== normalize(original.username) ||
      normalize(bio) !== normalize(original.bio);

    const avatarChanged = !!avatarFile;

    return fieldsChanged || avatarChanged;
  };

  // ✅ ONE SAVE FUNCTION: avatar + name/surname/username/bio/phone
  const handleSaveProfile = async () => {
    if (!userId) return;
    if (!hasChanges()) return; // ✅ prevents running if nothing changed

    try {
      setAvatarUploading(true);

      let finalPhotoURL = original?.picture || userData?.picture || "";

      // ✅ 1) Upload avatar if user selected a new one
      if (avatarFile) {
        const fileExt = avatarFile.name?.split(".").pop() || "jpg";
        const filePath = `users/${userId}/avatar_${Date.now()}.${fileExt}`;
        const avatarRef = ref(storage, filePath);

        await uploadBytes(avatarRef, avatarFile);
        finalPhotoURL = await getDownloadURL(avatarRef);
      }

      // ✅ 2) Build only changed fields
      const updates = {};

      if (normalize(name) !== normalize(original?.name)) updates.name = normalize(name);
      if (normalize(surname) !== normalize(original?.surname)) updates.surname = normalize(surname);
      if (normalize(phone) !== normalize(original?.phone)) updates.phone = normalize(phone);
      if (normalize(username) !== normalize(original?.username)) updates.username = normalize(username);
      if (normalize(bio) !== normalize(original?.bio)) updates.bio = normalize(bio);

      // ✅ only set picture if avatar changed
      if (avatarFile) updates.picture = finalPhotoURL;

      // ✅ always set updatedAt
      updates.updatedAt = serverTimestamp();

      // ✅ 3) Save to Firestore
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, updates);

      // ✅ 4) Update local snapshot so Save buttons disable again
      setOriginal((prev) => ({
        ...(prev || {}),
        name,
        surname,
        phone,
        username,
        bio,
        picture: finalPhotoURL,
      }));

      // ✅ 5) Clear selected avatar after saving
      setAvatarFile(null);
      setAvatarPreview("");

      console.log("✅ Profile saved:", updates);
    } catch (error) {
      console.error("❌ Error saving profile:", error);
    } finally {
      setAvatarUploading(false);
    }
  };

  // ----------------------
  // SETTINGS (subcollection: users/{uid}/settings/main)
  // ----------------------
  const [settingsData, setSettingsData] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");

  // Listen to settings doc (real-time)
  useEffect(() => {
    if (!userId) return;

    const settingsRef = doc(db, "users", userId, "settings", "main");
    const unsub = onSnapshot(
      settingsRef,
      (snap) => {
        if (!snap.exists()) {
          setSettingsData(null);
          setSettingsDraft(null);
          return;
        }
        const data = snap.data();
        setSettingsData(data);
        setSettingsDraft(data); // keep UI in sync
      },
      (err) => console.error("Settings listener error:", err)
    );

    return () => unsub();
  }, [userId]);

  // Helper: set nested field in draft
  const setSetting = (path, value) => {
    setSettingsDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      let node = next;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return next;
    });
  };

  // Only enable Save if something changed
  const hasSettingsChanges = useMemo(() => {
    if (!settingsDraft || !settingsData) return false;
    return JSON.stringify(settingsDraft) !== JSON.stringify(settingsData);
  }, [settingsDraft, settingsData]);

  const handleSaveSettings = async () => {
    if (!userId) return;
    if (!hasSettingsChanges) return;

    try {
      setSettingsSaving(true);
      setSettingsMsg("");

      const settingsRef = doc(db, "users", userId, "settings", "main");
      const { createdAt, ...safeDraft } = settingsDraft || {}; // keep createdAt stable

      await updateDoc(settingsRef, {
        ...safeDraft,
        updatedAt: serverTimestamp(),
      });

      setSettingsMsg("✅ Settings saved");
      setTimeout(() => setSettingsMsg(""), 2000);
    } catch (err) {
      console.error("Error saving settings:", err);
      setSettingsMsg("❌ Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const Security = ({ userId, userData }) => {
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
  
    // Change password inputs (optional)
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
  
    const email = useMemo(() => auth.currentUser?.email || userData?.email || "", [userData]);
  
    const safeSet = (type, text) => {
      if (type === "msg") setMsg(text);
      if (type === "err") setErr(text);
    };
  
    const clearAlerts = () => {
      setMsg("");
      setErr("");
    };
  
    // ✅ Reset password (sends email)
    const handleResetPassword = async () => {
      clearAlerts();
      if (!email) {
        safeSet("err", "No email found for this account.");
        return;
      }
  
      try {
        setBusy(true);
        await sendPasswordResetEmail(auth, email);
        safeSet("msg", `Password reset email sent to ${email}`);
      } catch (e) {
        safeSet("err", e?.message || "Failed to send reset email.");
      } finally {
        setBusy(false);
      }
    };
  
    // ✅ Re-auth helper for sensitive actions (update password / delete account)
    const reauthIfPossible = async () => {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user found.");
  
      // If user signed in with email/password, we can reauth using currentPassword.
      const providerId = user.providerData?.[0]?.providerId;
  
      if (providerId === "password") {
        if (!currentPassword) {
          throw new Error("Please enter your current password to confirm.");
        }
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        return;
      }
  
      // If Google/other provider: reauth needs a popup flow.
      // Keep it simple: show a message so you can implement later.
      throw new Error(
        "To change password or delete account for Google sign-in, please re-login with Google (reauth popup) first."
      );
    };
  
    // ✅ Change password (requires recent login)
    const handleChangePassword = async () => {
      clearAlerts();
  
      if (!newPassword || newPassword.length < 6) {
        safeSet("err", "New password must be at least 6 characters.");
        return;
      }
  
      try {
        setBusy(true);
        await reauthIfPossible();
        await updatePassword(auth.currentUser, newPassword);
  
        setCurrentPassword("");
        setNewPassword("");
        safeSet("msg", "Password updated successfully.");
      } catch (e) {
        safeSet("err", e?.message || "Failed to update password.");
      } finally {
        setBusy(false);
      }
    };
  
    // ✅ Sign out (this device)
    const handleSignOut = async () => {
      clearAlerts();
      try {
        setBusy(true);
        await signOut(auth);
        safeSet("msg", "Signed out.");
      } catch (e) {
        safeSet("err", e?.message || "Failed to sign out.");
      } finally {
        setBusy(false);
      }
    };
  
    /**
     * ✅ Sign out of all devices
     * Real way: revoke refresh tokens via Admin SDK (Cloud Function) -> forces re-login everywhere.
     * Client-only apps cannot reliably do this.
     * For now: show a message + sign out locally.
     */
    const handleSignOutAllDevices = async () => {
      clearAlerts();
      try {
        setBusy(true);
  
        // TODO (backend): call a Cloud Function like `revokeTokens` for auth.currentUser.uid
        // await httpsCallable(functions, "revokeTokens")({ uid: auth.currentUser.uid });
  
        await signOut(auth);
        safeSet(
          "msg",
          "Signed out on this device. To sign out everywhere, add a Cloud Function to revoke tokens (I can help you with that next)."
        );
      } catch (e) {
        safeSet("err", e?.message || "Failed to sign out of all devices.");
      } finally {
        setBusy(false);
      }
    };
  
    // ✅ Delete account (requires recent login)
    const handleDeleteAccount = async () => {
      clearAlerts();
      const user = auth.currentUser;
      if (!user) {
        safeSet("err", "No authenticated user found.");
        return;
      }
  
      const ok = window.confirm(
        "This will permanently delete your account. This cannot be undone. Continue?"
      );
      if (!ok) return;
  
      try {
        setBusy(true);
        await reauthIfPossible();
        await deleteUser(user);
  
        safeSet("msg", "Account deleted.");
      } catch (e) {
        safeSet("err", e?.message || "Failed to delete account.");
      } finally {
        setBusy(false);
      }
    };
  
    const providerLabel =
      auth.currentUser?.providerData?.[0]?.providerId === "password"
        ? "Email & Password"
        : auth.currentUser?.providerData?.[0]?.providerId || "Unknown";
  
    return (
      <div className="profile-view">
        <h2>Security</h2>
  
        {(msg || err) && (
          <div className={`security-alert ${err ? "error" : "success"}`}>
            {err || msg}
          </div>
        )}
  
        <div className="security-card">
          <h3>Account</h3>
  
          <div className="security-row">
            <span>Email</span>
            <strong>{email || "—"}</strong>
          </div>
  
          <div className="security-row">
            <span>Sign-in method</span>
            <strong>{providerLabel}</strong>
          </div>
        </div>
  
        <div className="security-card">
          <h3>Password</h3>
  
          <button className="danger-btn" onClick={handleResetPassword} disabled={busy}>
            {busy ? "Please wait..." : "Send password reset email"}
          </button>
  
          {/* If you only want reset-email and not change-password UI, you can remove this block */}
          <div className="security-form">
            <input
              type="password"
              placeholder="Current password (required for email sign-in)"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
            />
            <input
              type="password"
              placeholder="New password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
            />
  
            <button className="save-profile" onClick={handleChangePassword} disabled={busy}>
              {busy ? "Saving..." : "Update password"}
            </button>
          </div>
        </div>
  
        <div className="security-card">
          <h3>Sessions</h3>
  
          <button className="save-profile" onClick={handleSignOut} disabled={busy}>
            Sign out (this device)
          </button>
  
          <button className="danger-outline-btn" onClick={handleSignOutAllDevices} disabled={busy}>
            Sign out of all devices
          </button>
        </div>
  
        <div className="security-card">
          <h3>Danger zone</h3>
          <button className="danger-btn" onClick={handleDeleteAccount} disabled={busy}>
            Delete account
          </button>
        </div>

        <p>Kairos Policies</p>

        {/* LEGAL LINKS (Landing page footer) */}
        <div className="legal-links">
              <button
                className="legal-link-btn"
                onClick={() => navigate("/privacy-policy")}
                type="button"
              >
                Privacy Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/terms-and-conditions")}
                type="button"
              >
                Terms & Conditions
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/cookie-policy")}
                type="button"
              >
                Cookie Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/refund-policy")}
                type="button"
              >
                Refund Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/acceptable-use-policy")}
                type="button"
              >
                Acceptable Use
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/ai-use-policy")}
                type="button"
              >
                AI Use & Content Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/subscription-policy")}
                type="button"
              >
                Subscription Policy
              </button>
            </div>
      </div>
      );
    }

    const renderView = () => {
      switch (activeView) {
        case "profile":
          return (
            <div className="profile-view">
              <h2>Profile</h2>

              <div className="profile-field">

                {/* ✅ Controlled inputs (so we can save + detect changes) */}
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Display Name"
                />

                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Surname"
                />

                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Number"
                />

                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                />

                <textarea
                  className="profile-bio-input"
                  rows={4}
                  placeholder="Tell us a little about yourself..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </div>

              <div className="profile-field">
                <label>Email</label>
                <input type="email" disabled value={userData?.email || ""} />
              </div>

              {/* ✅ Save Changes uses the SAME save function */}
              <button
                className="save-profile"
                onClick={handleSaveProfile} // ✅ ADDED
                disabled={avatarUploading || !hasChanges()} // ✅ ADDED
              >
                {avatarUploading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          );

        case "settings":
            return (
              <div className="profile-view">
                <h2>Settings</h2>
          
                {!settingsDraft ? (
                  <p style={{ opacity: 0.7 }}>Loading settings...</p>
                ) : (
                  <>
                    {settingsMsg && <div className="settings-msg">{settingsMsg}</div>}
          
                    {/* APPEARANCE */}
                    <div className="settings-group">
                      <h3 className="settings-title">Appearance</h3>
          
                      <div className="toggle-row">
                        <span>Theme</span>
                        <select
                          className="settings-select"
                          value={settingsDraft.appearance?.theme || "system"}
                          onChange={(e) => setSetting(["appearance", "theme"], e.target.value)}
                        >
                          <option value="system">System</option>
                          <option value="light">Light</option>
                          <option value="dark">Dark</option>
                        </select>
                      </div>
          
                      <div className="toggle-row">
                        <span>Reduce motion</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.appearance?.reduceMotion}
                          onChange={(e) =>
                            setSetting(["appearance", "reduceMotion"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>High contrast</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.appearance?.highContrast}
                          onChange={(e) =>
                            setSetting(["appearance", "highContrast"], e.target.checked)
                          }
                        />
                      </div>
                    </div>
          
                    {/* PREFERENCES */}
                    <div className="settings-group">
                      <h3 className="settings-title">Preferences</h3>
          
                      <div className="toggle-row">
                        <span>Language</span>
                        <select
                          className="settings-select"
                          value={settingsDraft.preferences?.language || "en"}
                          onChange={(e) => setSetting(["preferences", "language"], e.target.value)}
                        >
                          <option value="en">English</option>
                          <option value="zu">isiZulu</option>
                          <option value="xh">isiXhosa</option>
                          <option value="st">Sesotho</option>
                          <option value="tn">Setswana</option>
                          <option value="nso">Sepedi</option>
                          <option value="af">Afrikaans</option>
                        </select>
                      </div>
          
                      <div className="toggle-row">
                        <span>Timezone</span>
                        <input
                          className="settings-input"
                          type="text"
                          value={settingsDraft.preferences?.timezone || ""}
                          onChange={(e) =>
                            setSetting(["preferences", "timezone"], e.target.value)
                          }
                          placeholder="e.g. Africa/Johannesburg"
                        />
                      </div>
                    </div>
          
                    {/* EDITOR & AI */}
                    <div className="settings-group">
                      <h3 className="settings-title">Editor & AI</h3>
          
                      <div className="toggle-row">
                        <span>Autosave</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.editor?.autosave}
                          onChange={(e) => setSetting(["editor", "autosave"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Autosave interval (seconds)</span>
                        <input
                          className="settings-input"
                          type="number"
                          min={5}
                          max={120}
                          value={settingsDraft.editor?.autosaveIntervalSec ?? 10}
                          onChange={(e) =>
                            setSetting(["editor", "autosaveIntervalSec"], Number(e.target.value))
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Show grid</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.editor?.showGrid}
                          onChange={(e) => setSetting(["editor", "showGrid"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Snap to grid</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.editor?.snapToGrid}
                          onChange={(e) => setSetting(["editor", "snapToGrid"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>AI suggestions</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.editor?.aiSuggestions}
                          onChange={(e) => setSetting(["editor", "aiSuggestions"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Auto-apply safe AI edits</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.editor?.aiAutoApplySafeEdits}
                          onChange={(e) =>
                            setSetting(["editor", "aiAutoApplySafeEdits"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Default AI model</span>
                        <select
                          className="settings-select"
                          value={settingsDraft.editor?.defaultAiModel || "gpt-4.1"}
                          onChange={(e) =>
                            setSetting(["editor", "defaultAiModel"], e.target.value)
                          }
                        >
                          <option value="gpt-4.1">GPT-4.1</option>
                          <option value="gpt-4o-mini">GPT-4o mini</option>
                          <option value="gpt-5">GPT-5</option>
                        </select>
                      </div>
                    </div>
          
                    {/* NOTIFICATIONS */}
                    <div className="settings-group">
                      <h3 className="settings-title">Notifications</h3>
          
                      <h4 className="settings-subtitle">Email</h4>
          
                      <div className="toggle-row">
                        <span>Product updates</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.email?.productUpdates}
                          onChange={(e) =>
                            setSetting(["notifications", "email", "productUpdates"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Tips & tutorials</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.email?.tipsAndTutorials}
                          onChange={(e) =>
                            setSetting(["notifications", "email", "tipsAndTutorials"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Publish status</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.email?.publishStatus}
                          onChange={(e) =>
                            setSetting(["notifications", "email", "publishStatus"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Billing</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.email?.billing}
                          onChange={(e) =>
                            setSetting(["notifications", "email", "billing"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Security alerts</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.email?.securityAlerts}
                          onChange={(e) =>
                            setSetting(["notifications", "email", "securityAlerts"], e.target.checked)
                          }
                        />
                      </div>
          
                      <h4 className="settings-subtitle">In-app</h4>
          
                      <div className="toggle-row">
                        <span>Product updates</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.inApp?.productUpdates}
                          onChange={(e) =>
                            setSetting(["notifications", "inApp", "productUpdates"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Publish status</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.inApp?.publishStatus}
                          onChange={(e) =>
                            setSetting(["notifications", "inApp", "publishStatus"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Billing</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.inApp?.billing}
                          onChange={(e) =>
                            setSetting(["notifications", "inApp", "billing"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Security alerts</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.notifications?.inApp?.securityAlerts}
                          onChange={(e) =>
                            setSetting(["notifications", "inApp", "securityAlerts"], e.target.checked)
                          }
                        />
                      </div>
                    </div>
          
                    {/* PRIVACY */}
                    <div className="settings-group">
                      <h3 className="settings-title">Privacy</h3>
          
                      <div className="toggle-row">
                        <span>Allow analytics</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.privacy?.analytics}
                          onChange={(e) => setSetting(["privacy", "analytics"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Personalization</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.privacy?.personalization}
                          onChange={(e) =>
                            setSetting(["privacy", "personalization"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Crash reports</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.privacy?.crashReports}
                          onChange={(e) => setSetting(["privacy", "crashReports"], e.target.checked)}
                        />
                      </div>
                    </div>
          
                    {/* MARKETING CONSENTS */}
                    <div className="settings-group">
                      <h3 className="settings-title">Marketing consents</h3>
          
                      <div className="toggle-row">
                        <span>Email marketing</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.marketingConsents?.email}
                          onChange={(e) => setSetting(["marketingConsents", "email"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>SMS marketing</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.marketingConsents?.sms}
                          onChange={(e) => setSetting(["marketingConsents", "sms"], e.target.checked)}
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>WhatsApp marketing</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.marketingConsents?.whatsapp}
                          onChange={(e) =>
                            setSetting(["marketingConsents", "whatsapp"], e.target.checked)
                          }
                        />
                      </div>
          
                      <div className="toggle-row">
                        <span>Partner offers</span>
                        <input
                          type="checkbox"
                          checked={!!settingsDraft.marketingConsents?.partners}
                          onChange={(e) =>
                            setSetting(["marketingConsents", "partners"], e.target.checked)
                          }
                        />
                      </div>
                    </div>
          
                    {/* SAVE */}
                    <button
                      type="button"
                      className="save-profile"
                      onClick={handleSaveSettings}
                      disabled={!hasSettingsChanges || settingsSaving}
                    >
                      {settingsSaving ? "Saving..." : "Save Settings"}
                    </button>
                  </>
                )}
              </div>
            );
        
        case "subscription":
          return (
            <Subscription />
          );

        case "security":
          return (
            <Security
              userId={userId}
              userData={userData}
            />
          );

        default:
          return null;
      }
    };

    return (
      <section className="profile-page-section">
        <div className="profile-glass">
          {/* SIDEBAR (DESKTOP) */}
          <aside className="profile-sidebar">
            <div className="profile-avatar">
                <div className="profile-avatar-uploader">
                  <div className="avatar-wrap">
                    <img
                      className="avatar-img"
                      src={avatarPreview || userData?.picture || "https://i.pravatar.cc/120"}
                      alt="Profile"
                    />

                    {/* Edit icon button */}
                    <button
                      type="button"
                      className="avatar-edit-btn"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Change profile picture"
                      title="Change picture"
                    >
                      <FaPen />
                    </button>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePickAvatar}
                      style={{ display: "none" }}
                    />
                  </div>

                  {/* ✅ Save button (now saves avatar + everything) */}
                  {activeView !== "profile" && 
                    (hasChanges() && (
                      <button
                        type="button"
                        className="avatar-save-btn"
                        onClick={handleSaveProfile} // ✅ ADDED
                        disabled={avatarUploading || !hasChanges()} // ✅ ADDED
                      >
                        {avatarUploading ? "Saving..." : "Save"}
                      </button>
                    ))
                  } 
              </div>
              <div>
                <h3>{userData?.name || "User"}</h3>
                <p className="email">{userData?.email}</p>
              </div>
              <FaTimes className="mobile-close-profile" onClick={onClose} />
            </div>

            <nav className="profile-menu">
              <button
                className={activeView === "profile" ? "active" : ""}
                onClick={() => setActiveView("profile")}
              >
                <FaUser /> Profile
              </button>

              <button
                className={activeView === "settings" ? "active" : ""}
                onClick={() => setActiveView("settings")}
              >
                <FaCog /> Settings
              </button>

              <button
                className={activeView === "subscription" ? "active" : ""}
                onClick={() => setActiveView("subscription")}
              >
                <FaCreditCard /> Subscription
              </button>

              <button
                className={activeView === "security" ? "active" : ""}
                onClick={() => setActiveView("security")}
              >
                <FaShieldAlt /> Security
              </button>
            </nav>
          </aside>

          {/* CONTENT */}
          <main className="profile-content">
            <FaTimes className="close-profile" onClick={onClose} />

            {/* MOBILE NAV */}
            <div className="profile-mobile-tabs">
              <button
                className={activeView === "profile" ? "active" : ""}
                onClick={() => setActiveView("profile")}
              >
                <FaUser />
                Profile
              </button>

              <button
                className={activeView === "settings" ? "active" : ""}
                onClick={() => setActiveView("settings")}
              >
                <FaCog />
                Settings
              </button>

              <button
                className={activeView === "subscription" ? "active" : ""}
                onClick={() => setActiveView("subscription")}
              >
                <FaCreditCard />
                Plan
              </button>

              <button
                className={activeView === "security" ? "active" : ""}
                onClick={() => setActiveView("security")}
              >
                <FaShieldAlt />
                Security
              </button>
            </div>

            {renderView()}
          </main>
        </div>
      </section>
    );
};

export default Profile;
